import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useState } from 'react';
import {
  LayoutDashboard,
  Battery,
  Bell,
  Settings,
  Gauge,
  Database,
  Calendar,
  LineChart,
  Wrench,
  LogOut,
  Network,
  TrendingUp,
  KeyRound,
  Users,
  Server,
  GitCompare,
  Euro,
  Sun,
  Mail,
  Cloud,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { TenantSwitcher } from './TenantSwitcher';

// Külgriba — minimaalne hele teema. Lame logo, kompaktsed lingid, üksik divider.

const navigation = [
  { name: 'Töölaud', href: '/', icon: LayoutDashboard },
  { name: 'Juhtimine', href: '/control', icon: Gauge, adminOnly: true },
  { name: 'Plaani võrdlus', href: '/plan-compare', icon: GitCompare, adminOnly: true },
  { name: 'Börsihind', href: '/borsihind', icon: TrendingUp },
  { name: 'Seadmed', href: '/devices', icon: Battery },
  { name: 'Ajalugu', href: '/history', icon: LineChart },
  { name: 'Tulu ajalugu', href: '/economics-history', icon: Euro },
  { name: 'Päikese ajalugu', href: '/solar-history', icon: Sun },
  { name: 'Tulud ja Päike (Markedroid)', href: '/markedroid-history', icon: Cloud },
  {
    name: 'Hooldus', href: '/service', icon: Wrench,
    children: [
      { name: 'Ajalugu',     href: '/service/history' },
      { name: 'Seadistused', href: '/service/settings' },
      { name: 'Monitooring',  href: '/service/monitor' },
    ],
  },
  { name: 'Modbus', href: '/modbus', icon: Database },
  { name: 'Topoloogia', href: '/topology', icon: Network },
  { name: 'Alarmid', href: '/alarms', icon: Bell },
  { name: 'Teavitused', href: '/notifications', icon: Mail, adminOnly: true },
  { name: 'Kasutajad', href: '/users', icon: Users, adminOnly: true },
  { name: 'Süsteem', href: '/system', icon: Server, superOnly: true },
  { name: 'Seadistused', href: '/settings', icon: Settings, superOnly: true },
];

export function Sidebar({ connected, alarmCount = 0, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, viewTenant, viewTenantName } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwNew2, setPwNew2] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault?.();
    setPwErr(null);
    if (pwNew !== pwNew2) { setPwErr('Uued paroolid ei kattu'); return; }
    if (pwNew.length < 6)  { setPwErr('Uus parool peab olema vähemalt 6 tähemärki'); return; }
    setPwBusy(true);
    try {
      const r = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: pwCurrent, new_password: pwNew }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `${r.status}`);
      setPwOpen(false); setPwCurrent(''); setPwNew(''); setPwNew2('');
      alert('Parool muudetud edukalt.');
    } catch (e) {
      setPwErr(e.message);
    } finally {
      setPwBusy(false);
    }
  };
  return (
    <aside className="w-52 bg-white border-r border-[var(--border)] flex flex-col">
      {/* Logo */}
      <div className="h-14 flex items-center px-5 border-b border-[var(--border)]">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-zinc-900 text-sm tracking-tight">Rohepaneel</span>
          <span className="text-[10px] uppercase tracking-wider text-zinc-400">EMS</span>
        </div>
      </div>

      {/* Superadmini kliendivalija (ainult superadminile) */}
      <TenantSwitcher />

      {/* Navigatsioon */}
      <nav className="flex-1 px-2 py-3 space-y-px">
        {navigation.filter(item => (!item.adminOnly || user?.role === 'admin') && (!item.superOnly || user?.is_superadmin)).map((item) => {
          const sectionActive = item.children && location.pathname.startsWith(item.href);
          return (
            <div key={item.name}>
              <NavLink
                to={item.href}
                end={item.href === '/'}
                onClick={() => onNavigate?.()}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2.5 px-3 py-1.5 rounded text-[13px] font-medium transition-colors',
                    (isActive || sectionActive)
                      ? 'bg-zinc-100 text-zinc-900'
                      : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
                  )
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={1.75} />
                {item.name}
                {item.name === 'Alarmid' && alarmCount > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-medium bg-red-600 text-white rounded">
                    {alarmCount}
                  </span>
                )}
              </NavLink>
              {/* Alamlingid (kuvatakse kui sektsioon aktiivne) */}
              {item.children && sectionActive && (
                <div className="mt-px ml-6 space-y-px">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.name}
                      to={child.href}
                      end
                      className={({ isActive }) =>
                        clsx(
                          'flex items-center px-3 py-1 rounded text-[12px] transition-colors',
                          isActive
                            ? 'text-zinc-900 font-medium'
                            : 'text-zinc-500 hover:text-zinc-900'
                        )
                      }
                    >
                      {child.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Ühenduse staatus (BMS / PCS) */}
      <div className="px-4 py-3 border-t border-[var(--border)] space-y-1.5 text-[11px]">
        <div className="flex items-center justify-between">
          <span className="text-zinc-500">BMS</span>
          <span
            className={clsx(
              'w-1.5 h-1.5 rounded-full',
              connected?.bms ? 'bg-green-600' : 'bg-zinc-300'
            )}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-zinc-500">PCS</span>
          <span
            className={clsx(
              'w-1.5 h-1.5 rounded-full',
              connected?.pcs ? 'bg-green-600' : 'bg-zinc-300'
            )}
          />
        </div>
      </div>

      {/* Kasutaja + parool + logout */}
      {user && (
        <div className="px-2 py-2 border-t border-[var(--border)]">
          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-400">
            {user.username}{user.role && user.role !== 'operator' ? ` (${user.role})` : ''}
          </div>
          {user.tenant_id && (
            <div className="px-2 pb-1 text-[10px] tracking-wider text-zinc-400" title="Kliendi kontekst">
              {user.is_superadmin
                ? `⬡ ${viewTenant ? (viewTenantName || viewTenant) : 'Kõik kliendid'}`
                : `⬡ ${user.tenant_id}`}
            </div>
          )}
          <button
            onClick={() => setPwOpen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded text-[13px] font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
          >
            <KeyRound className="w-4 h-4" strokeWidth={1.75} />
            Muuda parool
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded text-[13px] font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
            Logi välja
          </button>
        </div>
      )}

      {/* Parooli muutmise modaal */}
      {pwOpen && (
        <div onClick={(e) => e.target === e.currentTarget && setPwOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <form onSubmit={handleChangePassword} style={{
            background: 'white', borderRadius: 6, padding: 24, width: 420, maxWidth: '90%',
            border: '1px solid #e5e7eb',
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 14 }}>Muuda oma parool</h3>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#6b7280', marginBottom: 4 }}>Praegune parool</label>
              <input type="password" autoFocus value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} required
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.88rem' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#6b7280', marginBottom: 4 }}>Uus parool (≥ 6 tähemärki)</label>
              <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} required minLength={6}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.88rem' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#6b7280', marginBottom: 4 }}>Korda uut parooli</label>
              <input type="password" value={pwNew2} onChange={(e) => setPwNew2(e.target.value)} required minLength={6}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.88rem' }} />
            </div>
            {pwErr && (
              <div style={{ padding: 8, marginBottom: 10, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 4, color: '#b91c1c', fontSize: '0.78rem' }}>
                {pwErr}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setPwOpen(false)} style={{
                padding: '6px 14px', borderRadius: 4, fontSize: '0.85rem',
                background: 'white', border: '1px solid #e5e7eb', cursor: 'pointer',
              }}>Tühista</button>
              <button type="submit" disabled={pwBusy} style={{
                padding: '6px 14px', borderRadius: 4, fontSize: '0.85rem',
                background: '#18181b', color: 'white', border: 'none', cursor: 'pointer',
                opacity: pwBusy ? 0.6 : 1,
              }}>{pwBusy ? 'Salvestan...' : 'Salvesta'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Versioon */}
      <div className="px-4 py-2 border-t border-[var(--border)] text-[10px] text-zinc-400 tabular-nums">
        v1.0.0
      </div>
    </aside>
  );
}

export default Sidebar;
